import { useState, useEffect } from 'react';
import { Check, Package, ClipboardList, User, MapPin, Phone, Clock, Loader2, AlertCircle, CheckCircle2, Square, CheckSquare, Coffee, UtensilsCrossed, Salad } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { API_URL } from '@/config/api';

// Packing checklist modal for kitchen staff
export default function OrderPackingChecklist({ 
  isOpen, 
  onClose, 
  order,
  token,
  onComplete
}) {
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [allChecked, setAllChecked] = useState(false);

  // Reset checklist when order changes
  useEffect(() => {
    if (order) {
      setCheckedItems(new Set());
      setAllChecked(false);
    }
  }, [order?.order_id]);

  // Check if all items are checked
  useEffect(() => {
    if (order?.items) {
      const totalItems = order.items.reduce((acc, item) => acc + 1 + (item.selected_sides?.length || 0), 0);
      setAllChecked(checkedItems.size === totalItems);
    }
  }, [checkedItems, order]);

  const toggleItem = (itemKey) => {
    setCheckedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemKey)) {
        next.delete(itemKey);
      } else {
        next.add(itemKey);
      }
      return next;
    });
  };

  const checkAllItems = () => {
    if (!order?.items) return;
    const allKeys = new Set();
    order.items.forEach((item, idx) => {
      allKeys.add(`item-${idx}`);
      item.selected_sides?.forEach((side, sideIdx) => {
        allKeys.add(`item-${idx}-side-${sideIdx}`);
      });
    });
    setCheckedItems(allKeys);
  };

  const uncheckAllItems = () => {
    setCheckedItems(new Set());
  };

  const handleMarkReady = async () => {
    if (!allChecked) {
      toast.error('Please check all items before marking as ready');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/${order.order_id}/packing-complete`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items_verified: order.items.map(item => ({
            item_id: item.item_id,
            name: item.name,
            quantity: item.quantity,
            sides: item.selected_sides?.map(s => s.name) || []
          }))
        })
      });

      if (!response.ok) throw new Error('Failed to update order');

      toast.success('Order marked as ready for collection');
      onComplete?.();
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!order) return null;

  const getItemIcon = (item) => {
    if (item.menu_type === 'canteen') return Coffee;
    if (item.menu_type === 'office_platters') return Package;
    return UtensilsCrossed;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col" data-testid="packing-checklist-modal" aria-describedby={undefined}>
        <DialogHeader className="border-b pb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <ClipboardList className="h-6 w-6 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-lg">Packing Checklist</DialogTitle>
              <p className="text-sm text-muted-foreground">Order #{order.order_id?.slice(-8)}</p>
            </div>
          </div>
        </DialogHeader>

        {/* Customer Info */}
        <div className="p-3 bg-muted/50 rounded-lg mt-2">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-foreground">{order.customer_name || 'Customer'}</span>
            </div>
            {order.delivery_info?.phone && (
              <div className="flex items-center gap-1.5">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{order.delivery_info.phone}</span>
              </div>
            )}
          </div>
          {order.delivery_info?.address && (
            <div className="flex items-center gap-1.5 mt-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{order.delivery_info.address}</span>
            </div>
          )}
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between py-3 border-b">
          <div className="flex items-center gap-2">
            {allChecked ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : (
              <AlertCircle className="h-5 w-5 text-amber-500" />
            )}
            <span className={`font-medium ${allChecked ? 'text-green-600 dark:text-green-400' : 'text-foreground'}`}>
              {checkedItems.size} / {order.items.reduce((acc, item) => acc + 1 + (item.selected_sides?.length || 0), 0)} items verified
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={allChecked ? uncheckAllItems : checkAllItems}
          >
            {allChecked ? 'Uncheck All' : 'Check All'}
          </Button>
        </div>

        {/* Items Checklist */}
        <div className="flex-1 overflow-y-auto py-2 space-y-3">
          {order.items.map((item, idx) => {
            const ItemIcon = getItemIcon(item);
            const itemKey = `item-${idx}`;
            const isItemChecked = checkedItems.has(itemKey);
            
            return (
              <div key={item.item_id || `pack-${idx}`} className="border rounded-lg overflow-hidden">
                {/* Main item */}
                <div 
                  className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                    isItemChecked ? 'bg-green-50 dark:bg-green-950/30' : 'hover:bg-muted/50'
                  }`}
                  onClick={() => toggleItem(itemKey)}
                  data-testid={`checklist-item-${idx}`}
                >
                  <Checkbox 
                    checked={isItemChecked}
                    onCheckedChange={() => toggleItem(itemKey)}
                    className="h-5 w-5"
                  />
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                      <ItemIcon className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h4 className={`font-medium ${isItemChecked ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                      {item.name}
                    </h4>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Badge variant="outline" className="text-xs">
                        x{item.quantity}
                      </Badge>
                      {item.menu_type && (
                        <span className="capitalize">{item.menu_type.replace('_', ' ')}</span>
                      )}
                    </div>
                  </div>
                  {isItemChecked && (
                    <Check className="h-5 w-5 text-green-500" />
                  )}
                </div>

                {/* Sides for this item */}
                {item.selected_sides?.length > 0 && (
                  <div className="bg-muted/30 border-t px-3 py-2 space-y-2">
                    <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <Salad className="h-3 w-3" />
                      Sides:
                    </span>
                    {item.selected_sides.map((side, sideIdx) => {
                      const sideKey = `item-${idx}-side-${sideIdx}`;
                      const isSideChecked = checkedItems.has(sideKey);
                      
                      return (
                        <div
                          key={sideIdx}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                            isSideChecked ? 'bg-green-100 dark:bg-green-900/30' : 'bg-background hover:bg-muted'
                          }`}
                          onClick={(e) => { e.stopPropagation(); toggleItem(sideKey); }}
                          data-testid={`checklist-side-${idx}-${sideIdx}`}
                        >
                          <Checkbox 
                            checked={isSideChecked}
                            onCheckedChange={() => toggleItem(sideKey)}
                            className="h-4 w-4"
                          />
                          <span className={`text-sm ${isSideChecked ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                            {side.name}
                          </span>
                          {side.is_extra && (
                            <Badge variant="secondary" className="text-[10px] ml-auto">Extra</Badge>
                          )}
                          {isSideChecked && (
                            <Check className="h-4 w-4 text-green-500 ml-auto" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Special instructions */}
                {item.special_instructions && (
                  <div className="bg-amber-50 dark:bg-amber-950/30 border-t px-3 py-2">
                    <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                      Note: {item.special_instructions}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <DialogFooter className="border-t pt-4 gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleMarkReady}
            disabled={saving || !allChecked}
            className={allChecked ? 'bg-green-600 hover:bg-green-700' : ''}
            data-testid="mark-ready-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            {allChecked ? 'Mark Ready for Collection' : 'Verify All Items First'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
